import { FC } from 'react';

import Box from '@material-ui/core/Box';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import TagLabel from 'components/TagLabel';
import { IFoodlaCategoryOption } from 'models/category';
import { IApoteaSuggestion, IIcaOnlineSuggestion } from 'models/product';

export type TAutoFillData = {
  label: string;
  confidence: number;
  reasoning?: string;
  category: IFoodlaCategoryOption;
};

export const mapApoteaAutoFillList = (data: IApoteaSuggestion, categories: IFoodlaCategoryOption[]) => {
  const result: TAutoFillData[] = [];

  const category = categories.find(({ id }) => id.trim() === data.category_id?.trim());
  if (category) {
    result.push({
      label: data.category,
      confidence: data.confidence,
      reasoning: data.reasoning,
      category,
    });
  }

  data.alternatives?.forEach(item => {
    result.push(...mapApoteaAutoFillList(item, categories));
  });

  return result;
};

export type TIcaInternalResponse = {
  EAN?: string | number;
  SegmentName: string;
  'Segment Number': number;
  'Sub-Category': string;
  'Sub-Category Number': number;
  Category: string;
  'Category Number': number;
  ItemSegment: string;
  confidence: number;
  reasoning: string;
  alternatives?: TIcaInternalResponse[];
};

export const mapIcaInteralAutoFillList = (data: TIcaInternalResponse, categories: IFoodlaCategoryOption[]) => {
  const result: TAutoFillData[] = [];

  const category = categories.find(({ name }) => name.trim() === data.SegmentName?.trim());
  if (category) {
    result.push({
      label: data.SegmentName,
      confidence: data.confidence,
      reasoning: data.reasoning,
      category,
    });
  }

  data.alternatives?.forEach(item => {
    result.push(...mapIcaInteralAutoFillList(item, categories));
  });

  return result;
};

export const mapIcaOnlineAutoFillList = (data: IIcaOnlineSuggestion, categories: IFoodlaCategoryOption[]) => {
  const result: TAutoFillData[] = [];

  // from: "Kött, Chark & Fågel (ATG Id:1) > Inälvsmat (ATG Id:4759) > Hjärta (ATG Id:4760)",
  // to: ["Kött, Chark & Fågel (ATG Id:1)", "Inälvsmat (ATG Id:4759)", "Hjärta (ATG Id:4760)"]
  const categoryNameList = data.category.split('>').map(item => item.trim());

  let foundCategories = categoryNameList.reverse().map(item => {
    const match = item.match(/\(ATG Id:(\d+)\)/) || [];
    // from: Hjärta (ATG Id:4760)
    // to: 4760
    const id = match[1];
    return categories.find(item => id === item.id);
  });

  const category = foundCategories.filter(item => !!item)[0];
  if (category) {
    result.push({
      label: data.category,
      confidence: data.confidence,
      reasoning: data.reasoning,
      category,
    });
  }

  data.alternatives?.forEach(item => {
    result.push(...mapIcaOnlineAutoFillList(item, categories));
  });

  return result;
};

interface AutoFillCategoryTagsProps {
  readOnly?: boolean;
  selectedCategory?: IFoodlaCategoryOption;
  list: TAutoFillData[];
  onSelect: (category: IFoodlaCategoryOption) => void;
}

const AutoFillCategoryTags: FC<AutoFillCategoryTagsProps> = ({ readOnly, selectedCategory, list, onSelect }) => {
  return (
    <div>
      <Box mb={0.5} fontWeight={700}>
        Alternativ
      </Box>

      {list.length ? (
        <Box display="flex" flexWrap="wrap" gridGap={8}>
          {list.map((tagData, index) => {
            return (
              <TagLabel
                key={index}
                readOnly={readOnly}
                withContent
                size="sm"
                type={selectedCategory?.id === tagData.category.id ? 'success' : 'info'}
                label={tagData.label}
                icon={
                  <Box
                    sx={{
                      mr: 0.25,
                      bgcolor: '#C6CBE9',
                      borderRadius: 4,
                      fontSize: 11,
                      lineHeight: '11px',
                      padding: '2px 4px',
                    }}
                  >
                    {tagData.confidence}
                  </Box>
                }
                onClick={() => onSelect(tagData.category)}
              >
                <Box mt={0.25}>{tagData.reasoning}</Box>
              </TagLabel>
            );
          })}
        </Box>
      ) : (
        <TagLabel
          readOnly={readOnly}
          type="warn"
          icon={<InfoOutlined fontSize="small" />}
          label="Vi kunde inte hitta en tydlig kategori för den här produkten. Kontrollera att informationen är korrekt och försök igen, eller välj kategori manuellt."
        />
      )}
    </div>
  );
};

export default AutoFillCategoryTags;
